import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import { Search, Plus, MoreVertical } from 'lucide-react'
import AppSidebar from '@/components/AppSidebar'
import DataSidebar from '@/components/DataSidebar'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { IntuiTooltip } from '@/components/intueri/IntuiTooltip'
import { apiService, type Interview } from '@/service/api'
import CreateTopicDialog from './CreateTopicDialog'

type TopicStatus = 'in_progress' | 'published' | 'unpublished'

const STATUS_OPTIONS: { value: TopicStatus | 'all'; labelKey: string; defaultLabel: string }[] = [
  { value: 'all', labelKey: 'hek:status.all', defaultLabel: 'All Status' },
  { value: 'in_progress', labelKey: 'hek:status.in_progress', defaultLabel: 'In Progress' },
  { value: 'published', labelKey: 'hek:status.published', defaultLabel: 'Published' },
  { value: 'unpublished', labelKey: 'hek:status.unpublished', defaultLabel: 'Unpublished' },
]

/**
 * UI-20 (Pegah): "Style of buttons is different in each page, must be
 * consistent everywhere".
 *
 * These carried a dot and a ring; the Data Catalog's status pill
 * (CatalogTableV2.tsx) is a plain tinted pill with neither. Same control, two
 * treatments, which is what the report is about — so the ring and the dot are
 * gone and the tint carries the state on its own.
 */
const STATUS_BADGE: Record<
  TopicStatus,
  { className: string; labelKey: string; defaultLabel: string }
> = {
  in_progress: {
    className: 'bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300',
    labelKey: 'hek:status.in_progress',
    defaultLabel: 'In Progress',
  },
  published: {
    className: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
    labelKey: 'hek:status.published',
    defaultLabel: 'Published',
  },
  unpublished: {
    className: 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300',
    labelKey: 'hek:status.unpublished',
    defaultLabel: 'Unpublished',
  },
}

// Backend exposes COMPLETED / PENDING / ANALYZING (intai/topic/schemas.py).
// Map onto the Figma's three-state badge set; "Unpublished" has no backend
// equivalent yet but stays in the filter so the UI lines up with the design.
//
// ANALYZING and PENDING deliberately share one label here. This list is a
// coarser view of the same interviews the topic's Interviews tab shows, and
// that tab keeps ANALYZING as its own muted state (InterviewsTab.tsx). So the
// two screens are different granularities of one field, not a disagreement.
// Said out loud because the collapse is invisible in the code below, and a
// reader comparing the two screens will otherwise read it as a bug.
const mapBackendStatus = (s: string): TopicStatus => {
  const upper = (s || '').toUpperCase()
  if (upper === 'COMPLETED') return 'published'
  // ANALYZING, PENDING and anything unrecognised all read as "In Progress":
  // the coarser of two truthful labels is the safe one for a list.
  return 'in_progress'
}

const formatDate = (iso: string) => {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })
}

/** Shown when an interview has no summary yet. Named so the description cell
 *  can tell "nothing to read" from real text and skip the hover tooltip. */
const EMPTY_DESCRIPTION = '—'

interface TopicRow {
  id: string
  fileName: string
  interviewer: string
  date: string
  description: string
  status: TopicStatus
}

const toRow = (iv: Interview): TopicRow => ({
  id: iv.id,
  fileName: iv.topic || iv.id,
  interviewer: iv.experts?.[0]?.name || iv.experts?.[0]?.email || '—',
  date: iv.interview_start_date,
  description: iv.meeting_summary?.trim() || EMPTY_DESCRIPTION,
  status: mapBackendStatus(iv.status),
})

const HumanExpertKnowledgePage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<TopicStatus | 'all'>('all')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [rows, setRows] = useState<TopicRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [pendingDelete, setPendingDelete] = useState<TopicRow | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        setLoading(true)
        const data = await apiService.listInterviews()
        if (cancelled) return
        setRows(data.map(toRow))
        setError(null)
      } catch (err) {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load topics')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [reloadKey])

  const topics = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter((row) => {
      if (statusFilter !== 'all' && row.status !== statusFilter) return false
      if (!q) return true
      return (
        row.fileName.toLowerCase().includes(q) ||
        row.interviewer.toLowerCase().includes(q) ||
        row.description.toLowerCase().includes(q)
      )
    })
  }, [rows, search, statusFilter])

  const allSelected = topics.length > 0 && topics.every((tt) => selectedIds.has(tt.id))
  const someSelected = topics.some((tt) => selectedIds.has(tt.id)) && !allSelected

  const toggleAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (allSelected) {
        topics.forEach((tt) => next.delete(tt.id))
      } else {
        topics.forEach((tt) => next.add(tt.id))
      }
      return next
    })
  }

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return
    setDeleting(true)
    try {
      await apiService.deleteInterview(pendingDelete.id)
      toast.success(
        t('hek:delete.success', {
          name: pendingDelete.fileName,
          defaultValue: 'Deleted "{{name}}"',
        }),
      )
      setSelectedIds((prev) => {
        if (!prev.has(pendingDelete.id)) return prev
        const next = new Set(prev)
        next.delete(pendingDelete.id)
        return next
      })
      setPendingDelete(null)
      setReloadKey((k) => k + 1)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete topic'
      toast.error(
        t('hek:delete.failed', {
          message,
          defaultValue: 'Failed to delete topic: {{message}}',
        }),
      )
    } finally {
      setDeleting(false)
    }
  }

  // Backend has no PUT/PATCH /v1/interviews/{id} endpoint yet, so Edit is
  // surfaced as a not-yet-supported toast. The dropdown item is kept in the
  // menu for design parity with Figma and to make the gap visible to the
  // backend team in QA. Wire up when the endpoint lands.
  const handleEditUnsupported = () => {
    toast.info(
      t('hek:edit.unsupported', {
        defaultValue: 'Editing is not available yet.',
      }),
    )
  }

  return (
    <div className="flex h-screen">
      <AppSidebar />
      <DataSidebar />
      <div className="flex flex-1 flex-col min-w-0">
        {/* Top row: title */}
        <header className="flex h-[60px] shrink-0 items-center border-b border-border bg-background px-6 dark:bg-dark-card">
          {/* UI-67: no glyph beside the title — see AppTopbar. */}
          <div className="flex items-center flex-1">
            <span className="text-sm font-medium text-foreground">
              {/* UI-67: one key for the rail and the header, so the two cannot
                  drift apart again. The product name is "Knowledge Capture"
                  (Pegah, 2026-10-01); the route and the key keep their old
                  spelling so shared links and stored flags still resolve. */}
              {t('nav:menu.expert_knowledge', { defaultValue: 'Knowledge Capture' })}
            </span>
          </div>
        </header>

        {/* Sub-toolbar */}
        <div className="flex h-[72px] items-center justify-between gap-3 border-b border-border bg-background px-6 shrink-0">
          <div className="flex items-center gap-3">
            <div className="relative w-[280px]">
              <Search className="size-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={String(t('hek:toolbar.search', { defaultValue: 'Search' }))}
                className="pl-8 h-9"
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as TopicStatus | 'all')}
            >
              <SelectTrigger className="h-9 w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {t(opt.labelKey, { defaultValue: opt.defaultLabel })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" className="h-9 gap-1.5" onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" />
            {t('hek:toolbar.new_topic', { defaultValue: 'New Topic' })}
          </Button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              {t('hek:loading', { defaultValue: 'Loading topics…' })}
            </div>
          ) : error ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-center px-6">
              <p className="text-sm font-semibold text-foreground">
                {t('hek:error.title', { defaultValue: 'Failed to load topics' })}
              </p>
              <p className="text-xs text-muted-foreground max-w-[400px]">{error}</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-background z-10">
                <tr className="border-b border-border text-left text-xs font-medium text-muted-foreground">
                  <th className="w-10 px-6 py-3">
                    <Checkbox
                      checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                      onCheckedChange={toggleAll}
                      aria-label={t('hek:table.select_all', { defaultValue: 'Select all' })}
                    />
                  </th>
                  <th className="px-3 py-3 font-medium">
                    {t('hek:table.file_name', { defaultValue: 'File name' })}
                  </th>
                  <th className="px-3 py-3 font-medium">
                    {t('hek:table.interviewer', { defaultValue: 'Interviewer' })}
                  </th>
                  <th className="px-3 py-3 font-medium">
                    {t('hek:table.date', { defaultValue: 'Date' })}
                  </th>
                  <th className="px-3 py-3 font-medium">
                    {t('hek:table.description', { defaultValue: 'Description' })}
                  </th>
                  <th className="px-3 py-3 font-medium">
                    {t('hek:table.status', { defaultValue: 'Status' })}
                  </th>
                  <th className="w-12 px-3 py-3 font-medium text-right pr-6">
                    {t('hek:table.actions', { defaultValue: 'Actions' })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {topics.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-16 text-center text-sm text-muted-foreground">
                      {rows.length === 0
                        ? t('hek:table.empty_all', {
                            defaultValue: 'No topics yet. Create one with "New Topic" to get started.',
                          })
                        : t('hek:table.empty_filtered', {
                            defaultValue: 'No topics match your filters.',
                          })}
                    </td>
                  </tr>
                ) : (
                  topics.map((row) => (
                    <TopicRowView
                      key={row.id}
                      row={row}
                      selected={selectedIds.has(row.id)}
                      onToggle={() => toggleRow(row.id)}
                      onOpen={() => navigate(`/human-expert-knowledge/${row.id}`)}
                      onEdit={handleEditUnsupported}
                      onDelete={() => setPendingDelete(row)}
                    />
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
      <CreateTopicDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => setReloadKey((k) => k + 1)}
      />
      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(open) => {
          if (!open && !deleting) setPendingDelete(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('hek:delete.title', { defaultValue: 'Delete topic?' })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('hek:delete.description', {
                name: pendingDelete?.fileName ?? '',
                defaultValue:
                  'This will permanently delete "{{name}}" and all of its uploaded documents. This cannot be undone.',
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              {t('common:cancel', { defaultValue: 'Cancel' })}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault()
                void handleConfirmDelete()
              }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting
                ? t('hek:delete.in_progress', { defaultValue: 'Deleting…' })
                : t('hek:delete.confirm', { defaultValue: 'Delete' })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

interface TopicRowViewProps {
  row: TopicRow
  selected: boolean
  onToggle: () => void
  onOpen: () => void
  onEdit: () => void
  onDelete: () => void
}

const TopicRowView = ({ row, selected, onToggle, onOpen, onEdit, onDelete }: TopicRowViewProps) => {
  const { t } = useTranslation()
  const badge = STATUS_BADGE[row.status]
  return (
    <tr
      onClick={onOpen}
      className="border-b border-border hover:bg-muted/40 transition-colors cursor-pointer"
    >
      <td className="px-6 py-3" onClick={(e) => e.stopPropagation()}>
        <Checkbox
          checked={selected}
          onCheckedChange={onToggle}
          aria-label={t('hek:table.select_row', { defaultValue: 'Select row' })}
        />
      </td>
      {/* UI-48 follow-up. These three columns were being squeezed to their
          narrowest fit, so short values broke across lines — "Safety /
          compliance", "August / 26, / 2026" — which is what made the row look
          ragged rather than full.

          Date and interviewer are short and fixed-shape, so they simply never
          wrap: a date split over three lines is harder to read than one that
          costs 20px more, and an interviewer is one name or one address.
          min-w on the file name is the softer version of the same idea — it
          keeps a normal name on one line without forbidding a wrap on a long
          one, which nowrap here would turn into a very wide column. */}
      <td className="px-3 py-3 font-medium text-foreground min-w-[180px]">{row.fileName}</td>
      <td className="px-3 py-3 text-foreground whitespace-nowrap">{row.interviewer}</td>
      <td className="px-3 py-3 text-foreground tabular-nums whitespace-nowrap">
        {formatDate(row.date)}
      </td>
      {/* UI-48 (Hasma): "Description column is truncated mid-sentence with no
          way to read the rest." It was capped at max-w-[400px], so it cut off
          at 400px however wide the window was — space the table had and did
          not use.

          Two parts to using that space. `w-full` claims whatever width the
          other columns leave, so the text gets the room the table actually
          has. `line-clamp-2` then spends it DOWNWARD as well: the description
          wraps to a second line before any ellipsis appears, which fits far
          more of a sentence than one line can and costs no row height here —
          the file-name and date columns already wrap to two lines, so the row
          rhythm is unchanged. Past two lines it clips, because a cell that
          grows without limit is the thing that would look bad.

          NOT `max-w-0` (WorkflowTable's pattern for its own description): that
          trick works because nowrap content cannot wrap, so the column's
          minimum is its full string. Once the text may wrap, a zero max-width
          lets the column collapse toward its longest WORD, which is the
          opposite of the ask.

          Revealing the rest is IntuiTooltip rather than a native `title`
          (Greptile review). A `title` fires on hover only, and on a span that
          nothing can focus — so a keyboard user had no way to reach it at all.
          Radix opens on focus as well as hover, and `tabIndex` is what makes
          that focus possible; TagListPanel wraps its truncated chips the same
          way but without the tabIndex, so it has the gap this one no longer
          does.

          Only a real description becomes a tooltip and a tab stop. The em-dash
          placeholder gets neither: there is nothing to read, and a stop per
          empty row is noise in a list. */}
      <td className="px-3 py-3 text-muted-foreground w-full">
        {row.description === EMPTY_DESCRIPTION ? (
          <span className="line-clamp-2">{row.description}</span>
        ) : (
          <IntuiTooltip content={row.description} side="top">
            <span
              tabIndex={0}
              className="line-clamp-2 cursor-default rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {row.description}
            </span>
          </IntuiTooltip>
        )}
      </td>
      <td className="px-3 py-3 align-middle">
        <span
          className={`inline-flex min-w-[104px] items-center justify-center rounded-full px-2.5 py-1 text-xs font-medium leading-none whitespace-nowrap ${badge.className}`}
        >
          {t(badge.labelKey, { defaultValue: badge.defaultLabel })}
        </span>
      </td>
      <td className="w-12 px-3 py-3 text-right pr-6" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={t('hek:table.row_actions', { defaultValue: 'Row actions' })}
            >
              <MoreVertical className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onOpen}>
              {t('hek:row_actions.view', { defaultValue: 'View details' })}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onEdit}>
              {t('hek:row_actions.edit', { defaultValue: 'Edit' })}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={onDelete}
              className="text-destructive focus:text-destructive"
            >
              {t('hek:row_actions.delete', { defaultValue: 'Delete' })}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </td>
    </tr>
  )
}

export default HumanExpertKnowledgePage
